import { useQuery } from '@tanstack/react-query';
import { RQAPICall, HttpError } from 'store/APIClient';
import { camelize } from 'helpers/syntaxHelper';
import {
    CHAPTER_FIELD,
    SUPER_OPERATION_NAME_FIELD,
    SUPER_OPERATION_FIELD,
    BUDGET_LINE_TABLE,
    BUDGET_LINE_BUDGET_ID,
    SECTION_FIELD,
    EXPENSE_INCOME_FIELD,
    BUDGET_LINE_ORG_CHART,
    BUDGET_LINE_ORG_CHART_FINANCE_ID,
    BUDGET_LINE_GESTIONNAIRE_ID,
    BUDGET_LINE_OPERATION_ID,
    DimensionType,
    BUDGET_LINES_VENTILATION_COLUMNS_PER_FIELD,
    ORDERED_DIMENSIONS_FOR_IMPUTATION_CONCATENATION,
    LINE_CODE_FIELD,
} from './constants';
import { useContext } from 'react';
import { isCustomBudgetProposal } from '../components/BudgetProposal/Resource/hooks/budgetProposal';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';

const LABELS = [DimensionType.Label];
const CODES = [DimensionType.Code];
const LABELS_AND_CODES = [DimensionType.Label, DimensionType.Code];
const LABELS_CODES_AND_OTHER = [
    DimensionType.Label,
    DimensionType.Code,
    DimensionType.Other,
];
/**
 * This constant should be used with the exposed hooks to pass the columns to exclude.
 */
export const EXCLUDED_DIMENSIONS_BY_CONTEXT = {
    BudgetProposal: [BUDGET_LINE_BUDGET_ID, SECTION_FIELD],
    ExpenseIncome: [EXPENSE_INCOME_FIELD],
    OrgChartFinance: [BUDGET_LINE_ORG_CHART, BUDGET_LINE_ORG_CHART_FINANCE_ID],
    Gestionnaire: [BUDGET_LINE_GESTIONNAIRE_ID],
    Operation: [BUDGET_LINE_OPERATION_ID],
    SuperOperation: [SUPER_OPERATION_NAME_FIELD, SUPER_OPERATION_FIELD],
} as const;

const DEFAULT_EXCLUDED_DIMENSIONS: string[] = [];
export const SIFAC_EXCLUDED_DIMENSIONS: string[] = [
    BUDGET_LINE_GESTIONNAIRE_ID,
    SUPER_OPERATION_FIELD,
    LINE_CODE_FIELD,
];

class ImputationDimensionsSet extends Set<string> {
    selectedDimensionTypes: DimensionType[];

    excludedDimensions: string[];

    constructor(
        initialValues: string[],
        selectedDimensionTypes: DimensionType[],
        excludedDimensions: string[],
    ) {
        super(initialValues);
        this.selectedDimensionTypes = selectedDimensionTypes;
        this.excludedDimensions = excludedDimensions;
    }

    /**
     * Adds the dimension if it is selected and a value is provided.
     *
     * @param dimType - The type of dimension to check
     * @param valueToAdd - The value to add if the dimension type is selected
     */
    addIfSelected(dimType: DimensionType, valueToAdd?: string): void {
        if (
            this.selectedDimensionTypes.includes(dimType) &&
            valueToAdd !== undefined
        ) {
            this.add(valueToAdd);
        }
    }

    /**
     * Adds the corresponding dimensions if none of the potential parent dimensions
     * are excluded.
     */
    addIfNotExcluded(
        // there are dimensions that have multiple parent dimensions,
        // like org_chart_finance, but the dimensions to add are the same.
        parentDimensions: string[],
        labelToAdd?: string,
        codeToAdd?: string,
        otherToAdd?: string,
    ): void {
        if (
            parentDimensions.some((dim) =>
                this.excludedDimensions.includes(dim),
            )
        ) {
            return;
        }
        const parentDim = parentDimensions[0]; // parent dimensions should lead to same children dimensions
        const dimensionConfig =
            BUDGET_LINES_VENTILATION_COLUMNS_PER_FIELD[
                parentDim as keyof typeof BUDGET_LINES_VENTILATION_COLUMNS_PER_FIELD
            ];

        if (dimensionConfig) {
            // Add configured dimensions if a parent configuration exists
            Object.entries(dimensionConfig).forEach(([dimType, value]) => {
                if (
                    this.selectedDimensionTypes.includes(
                        dimType as DimensionType,
                    )
                ) {
                    this.add(value);
                }
            });
        } else {
            // If no configuration exists, use the provided arguments to add dimensions
            this.addIfSelected(DimensionType.Code, codeToAdd);
            this.addIfSelected(DimensionType.Label, labelToAdd);
            this.addIfSelected(DimensionType.Other, otherToAdd);
        }
    }
}

type ImputationDimensionsQueryResult = {
    isFetching: boolean;
    isError: boolean;
    imputationDimensions: string[];
    error: HttpError | null;
};

export const useRawImputationDimensions =
    (): ImputationDimensionsQueryResult => {
        const {
            data: imputationRawDimensions,
            isFetching,
            isError,
            error,
        } = useQuery({
            queryKey: ['imputation_ventilation_dimensions'],
            queryFn: async () => {
                const response = await RQAPICall({
                    endpoint: 'budget_proposals/ventilation_columns',
                    method: 'GET',
                    postProcessingCallback: (data) => ({
                        data: data.data.map(camelize),
                    }),
                });
                return response.content.data;
            },
            meta: {
                useGlobalOnError: true,
            },
            initialData: [],

            // This is in most cases a bit overkill as the cache lives in-memory
            // and is emptied whenever the page reloads or the tab is closed.
            // TODO: persist this query data in local storage.
            // one day in milliseconds
            staleTime: 24 * 60 * 60 * 1000,

            // one day in milliseconds
            gcTime: 24 * 60 * 60 * 1000,

            // This is needed so that RQ fetches the data at least once before
            // caching it for a long time. Without it, the initial data is considered
            // fresh for as long as staleTime. The value is a timestamp.
            initialDataUpdatedAt: 0,
        });

        return {
            isFetching,
            isError,
            // @ts-expect-error [TS migration] to fix this, specify the generic type in the corresponding RQAPICall
            imputationDimensions: imputationRawDimensions,
            error,
        };
    };

/**
 * Imputation ventilation dimensions depend on the used finance software.
 * This function is useful to know what are the dimensions we need.
 */
const getImputationDimensions = (
    imputationRawDimensions: string[],
    selectedDimensionTypes: DimensionType[],
    excludedDimensions: string[] = DEFAULT_EXCLUDED_DIMENSIONS,
    isSchool: boolean = false,
): string[] => {
    // For school software we need to exclude some dimensions
    const updatedExcludedDimensions = [...excludedDimensions];
    if (isSchool) {
        updatedExcludedDimensions.push(...SIFAC_EXCLUDED_DIMENSIONS);
    }
    // We need a Set because some dimensions are considered codes and labels, but we do not want duplicates
    // when both codes and labels are required.
    // These fields should always be present at the start, no matter the software.
    const imputationDimensions = new ImputationDimensionsSet(
        [CHAPTER_FIELD],
        selectedDimensionTypes,
        updatedExcludedDimensions,
    );

    // We iterate through all the possible dimension types and check which were requested
    imputationRawDimensions.forEach((rawDim) => {
        const fullField = `${BUDGET_LINE_TABLE}.${rawDim}`;
        imputationDimensions.addIfNotExcluded([fullField]);
        if (fullField === BUDGET_LINE_OPERATION_ID) {
            imputationDimensions.addIfNotExcluded(
                [SUPER_OPERATION_FIELD, SUPER_OPERATION_NAME_FIELD],
                SUPER_OPERATION_NAME_FIELD,
                SUPER_OPERATION_FIELD,
            );
        }
    });

    // These fields should always be present at the end, no matter the software except for school software
    imputationDimensions.addIfNotExcluded([BUDGET_LINE_GESTIONNAIRE_ID]);
    imputationDimensions.addIfNotExcluded([
        BUDGET_LINE_ORG_CHART,
        BUDGET_LINE_ORG_CHART_FINANCE_ID,
    ]);
    return Array.from(imputationDimensions);
};

export const getImputationDimensionsLabels = (
    imputationRawDimensions: string[],
    excludedDimensions: string[] = DEFAULT_EXCLUDED_DIMENSIONS,
    isSchool: boolean = false,
): string[] =>
    getImputationDimensions(
        imputationRawDimensions,
        LABELS,
        excludedDimensions,
        isSchool,
    );

export const getImputationDimensionsCodes = (
    imputationRawDimensions: string[],
    excludedDimensions: string[] = DEFAULT_EXCLUDED_DIMENSIONS,
): string[] =>
    getImputationDimensions(imputationRawDimensions, CODES, excludedDimensions);

export const getImputationDimensionsLabelsAndCodes = (
    imputationRawDimensions: string[],
    excludedDimensions: string[] = DEFAULT_EXCLUDED_DIMENSIONS,
): string[] =>
    getImputationDimensions(
        imputationRawDimensions,
        LABELS_AND_CODES,
        excludedDimensions,
    );

export const getImputationDimensionsLabelsCodesAndOther = (
    imputationRawDimensions: string[],
    excludedDimensions: string[] = DEFAULT_EXCLUDED_DIMENSIONS,
): string[] =>
    getImputationDimensions(
        imputationRawDimensions,
        LABELS_CODES_AND_OTHER,
        excludedDimensions,
    );

export const getImputationAsString = (
    imputationRawDimensions: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((arg1: any) => string) => {
    const imputationDimensions = getImputationDimensionsCodes(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.SuperOperation,
        ],
    );
    const orderedImputationDimensions = imputationDimensions.sort(
        (a, b) =>
            ORDERED_DIMENSIONS_FOR_IMPUTATION_CONCATENATION.indexOf(a) -
            ORDERED_DIMENSIONS_FOR_IMPUTATION_CONCATENATION.indexOf(b),
    );

    return (imputation) =>
        orderedImputationDimensions
            .map((dim) => imputation[dim] ?? '')
            .join('-');
};

/**
 * Custom hook to get the imputation dimensions labels and codes, excluding certain dimensions based on whether
 * the budget proposal is custom or not.
 *
 * @returns An array of imputation dimension labels and codes.
 */
export const useGetImputationDimensions = (
    includeOther: boolean = false,
): string[] => {
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);
    const imputationRawDimensions = useContext(ImputationDimensionsContext);
    const excludedDimensions = isSchool ? SIFAC_EXCLUDED_DIMENSIONS : [];
    if (includeOther)
        return getImputationDimensionsLabelsCodesAndOther(
            imputationRawDimensions,
            excludedDimensions,
        );
    return getImputationDimensionsLabelsAndCodes(
        imputationRawDimensions,
        excludedDimensions,
    );
};
